<template>
  <div>
    <div class="page t-container">
      <section
        class="t-mt-8 t-flex-col t-space-y-8 lg:t-space-y-0 lg:t-flex-row"
        role="main"
      >
        <h2
          class="t-text-left t-text-2xl t-font-semibold t-mb-4 t-ml-4 md:t-ml-0"
        >
          Run Sheets
        </h2>
        <div
          class="t-flex t-flex-col t-space-y-8 t-items-center lg:t-w-full lg:t-mr-8"
        >
          <div class="t-flex t-flex-col t-w-full t-p-6 t-bg-white t-rounded-xl">
            <div class="t-flex t-mb-5 t-items-center">
              <label
                class="t-w-1/5 t-mb-0 t-text-gray-700 t-text-sm t-font-semibold"
                >Select Run Sheet for Carrier</label
              >
              <div class="t-w-4/5">
                <multiselect
                  v-model="runSheetAllocation"
                  :options="runSheets"
                  placeholder="Select a Run Sheet"
                  :custom-label="runSheetLabel"
                  track-by="id"
                  v-validate="'required'"
                  @input="handleRunSheetAllocation"
                >
                </multiselect>
                <p
                  v-show="errors.has('truck')"
                  class="t-text-red-500 t-text-sm"
                >
                  Please select the truck
                </p>
              </div>
            </div>
            <div class="t-w-full">
              <p v-if="!isMapLoading && runSheetAllocation" class="t-text-md">
                Loading...
              </p>
              <directions
                v-if="isMapLoading && runSheetAllocation"
                :origin="mapMovement.pickup"
                :destination="mapMovement.delivery"
                :latestPoint="mapMovement.latestPoint"
                :height="500"
              />
            </div>
          </div>

          <form
            @submit.prevent="postRunSheet"
            class="t-flex t-flex-col t-w-full t-p-6 t-bg-white t-rounded-xl"
          >
            <div class="t-flex t-my-2 t-items-center">
              <label
                class="t-w-1/5 t-mb-0 t-text-gray-700 t-text-sm t-font-semibold"
                >Run Sheet for Truck</label
              >
              <div class="t-w-4/5">
                <multiselect
                  v-model="truckAllocation"
                  :options="trucks"
                  placeholder="Allocate a truck"
                  :custom-label="nameWithLang"
                  track-by="id"
                  v-validate="'required'"
                  name="truck"
                  @input="getLegs"
                >
                </multiselect>
                <p
                  v-show="errors.has('truck')"
                  class="t-text-red-500 t-text-sm"
                >
                  Please select the truck
                </p>
              </div>
            </div>
            <div class="t-flex t-my-2 t-gap-3">
              <div class="t-flex t-w-1/2 t-items-center">
                <label
                  class="t-w-1/4 t-mb-0 t-text-gray-700 t-text-sm t-font-semibold"
                  >Start Date</label
                >
                <div class="t-w-3/4">
                  <date-picker
                    v-model="startDate"
                    name="start_date"
                    type="datetime"
                    style="width: 100%;"
                    format="YYYY-MM-DD HH:mm"
                    valueType="format"
                    :confirm="true"
                    v-validate="'required'"
                  ></date-picker>
                  <p
                    v-show="errors.has('start_date')"
                    class="t-text-red-500 t-text-sm"
                  >
                    Please select start date
                  </p>
                </div>
              </div>
              <div class="t-flex t-w-1/2 t-items-center">
                <label
                  class="t-w-1/4 t-mb-0 t-text-gray-700 t-text-sm t-font-semibold"
                  >End Date</label
                >
                <div class="t-w-3/4">
                  <date-picker
                    v-model="endDate"
                    name="end_date"
                    type="datetime"
                    style="width: 100%;"
                    format="YYYY-MM-DD HH:mm"
                    valueType="format"
                    :confirm="true"
                    v-validate="'required'"
                  ></date-picker>
                  <p
                    v-show="errors.has('end_date')"
                    class="t-text-red-500 t-text-sm"
                  >
                    Please select end date
                  </p>
                </div>
              </div>
            </div>
            <div class="t-flex t-my-2 t-gap-3">
              <div class="t-flex t-w-1/2 t-items-center">
                <label
                  class="t-w-1/4 t-mb-0 t-text-gray-700 t-text-sm t-font-semibold"
                  >Start Location</label
                >
                <div class="t-w-3/4">
                  <address-autocomplete
                    class="mx-input"
                    @location-changed="onStartLocationChanged"
                    @fieldKeyDown="onStartLocationTextChanged"
                  ></address-autocomplete>
                  <p
                    v-show="startLocationError"
                    class="t-text-red-500 t-text-sm"
                  >
                    Please enter start location
                  </p>
                </div>
              </div>
              <div class="t-flex t-w-1/2 t-items-center">
                <label
                  class="t-w-1/4 t-mb-0 t-text-gray-700 t-text-sm t-font-semibold"
                  >End Location</label
                >
                <div class="t-w-3/4">
                  <address-autocomplete
                    class="mx-input"
                    @location-changed="onEndLocationChanged"
                    @fieldKeyDown="onEndLocationTextChanged"
                  ></address-autocomplete>
                  <p v-show="endLocationError" class="t-text-red-500 t-text-sm">
                    Please enter end location
                  </p>
                </div>
              </div>
            </div>
            <div class="t-flex t-mt-2 t-gap-3">
              <div class="t-flex t-w-1/2 t-items-center">
                <label
                  class="t-w-1/4 t-mb-0 t-text-gray-700 t-text-sm t-font-semibold"
                  >Legs</label
                >
              </div>
              <div class="t-flex t-w-1/2 t-items-center">
                <label
                  class="t-w-1/4 t-mb-0 t-text-gray-700 t-text-sm t-font-semibold"
                  >Schedule</label
                >
              </div>
            </div>
            <div class="t-flex t-my-2 t-gap-3">
              <div
                class="t-flex t-flex-col t-w-1/2 t-border t-rounded-lg t-bg-white t-shadow-md"
              >
                <draggable
                  :list="legsData"
                  group="people"
                  @change="log"
                  class="t-flex t-flex-col t-h-full t-gap-3 t-p-3"
                >
                  <div
                    v-if="loadingLegs"
                    class="t-flex t-w-full t-text-sm t-p-3"
                  >
                    <p>Loading...</p>
                  </div>

                  <div
                    v-else
                    v-for="(element, index) in legsData"
                    class="t-flex t-w-full t-border t-border-gray-500 t-rounded-md t-shadow-md t-cursor-move t-text-sm t-p-3 t-items-center"
                    :key="index"
                  >
                    Leg #{{ element.number }} -
                    {{ element.stage === 1 ? "Pickup" : "Delivery" }}<br />
                    From : {{ element.pickup_location.name }} <br />
                    To : {{ element.delivery_location.name }} <br />
                  </div>

                  <div
                    v-if="!loadingLegs && legsData.length === 0"
                    class="t-flex t-w-full t-text-sm t-p-3"
                  >
                    <p>No legs to show</p>
                  </div>
                </draggable>
              </div>
              <div
                class="t-flex t-flex-col t-w-1/2 t-border t-rounded-lg t-bg-white t-shadow-md"
              >
                <draggable
                  :list="schedules"
                  group="people"
                  @change="log"
                  class="t-flex t-flex-col t-h-full t-gap-3 t-p-3"
                >
                  <div
                    class="t-flex t-w-full t-border t-border-gray-500 t-rounded-md t-shadow-md t-cursor-move t-text-sm t-p-3 t-items-center"
                    v-for="(element, index) in schedules"
                    :key="index"
                  >
                    Leg #{{ element.number }} -
                    {{ element.stage === 1 ? "Pickup" : "Delivery" }} - Order
                    #{{ index + 1 }} <br />
                    From : {{ element.pickup_location.name }} <br />
                    To : {{ element.delivery_location.name }} <br />
                  </div>
                </draggable>
              </div>
            </div>
            <div class="t-flex t-my-2 t-gap-3">
              <div class="t-flex t-w-1/2">
                <!-- pagination section  -->
              </div>
              <div class="t-flex t-w-1/2">
                <!-- pagination section  -->
              </div>
            </div>
            <div class="t-flex t-flex-row-reverse t-my-2 t-gap-3">
              <button
                class="t-bg-blue-500 hover:t-bg-blue-700 t-text-white t-font-bold t-py-2 t-px-4 t-rounded"
                type="submit"
              >
                Create Run Sheet
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import AddressAutocomplete from "../../components/form/EstimateAddressAutocomplete.vue";
import draggable from "vuedraggable";
import moment from "moment";
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import Multiselect from "vue-multiselect";
import Paginate from "vuejs-paginate";
import Directions from "../../components/maps/Directions.vue";

export default {
  name: "RunSheet",
  $_veeValidate: {
    validator: "new"
  },
  data() {
    return {
      truckAllocation: null,
      trucks: [],
      loadingTruck: false,
      startDate: null,
      endDate: null,
      startLocation: null,
      endLocation: null,
      startLocationText: "",
      endLocationText: "",
      schedules: [],
      loadingLegs: false,
      loadingRoute: false,
      routes: [],
      legs: null,
      legsData: [],
      pickupLegs: [],
      deliveryLegs: [],
      startLocationError: false,
      endLocationError: false,
      runSheetAllocation: null,
      runSheets: [],
      mapMovement: {
        rawMapMovements: { data: [] },
        pickup: { lat: null, lng: null },
        delivery: { lat: null, lng: null },
        latestPoint: { lat: null, lng: null }
      },
      // mapMovement: {
      //   rawMapMovements: { data: [] },
      //   pickup: { lat: -36.8508827, lng: 174.7644881 },
      //   delivery: { lat: -38.1445987, lng: 176.2377669 },
      //   latestPoint: { lat: null, lng: null }
      // },
      // mapMovement: {
      //   rawMapMovements: { data: [] },
      //   pickup: { lat: -37.735864, lng: 176.134839 },
      //   delivery: { lat: -37.726905, lng: 175.221317 },
      //   latestPoint: { lat: null, lng: null }
      // },
      isMapLoading: false
    };
  },
  order: 1,
  components: {
    draggable,
    DatePicker,
    AddressAutocomplete,
    Multiselect,
    Paginate,
    Directions
  },
  mounted() {
    this.loadTrucks();
    this.loadRunSheets();
  },
  created() {},
  methods: {
    loadRoute(startTime, endTime) {
      this.loadingRoute = true;
      api
        .get(
          "/carrier/gps/stops?truck_id=" +
            this.runSheetAllocation.truck_id +
            "&start_time=" +
            startTime +
            "&end_time=" +
            endTime +
            "&include=startPoint&sort=asc&previous=included"
        )
        .then(response => {
          this.routes = response.data.data;

          if (this.routes.length) {
            let lastRoute = this.routes[this.routes.length - 1];
            this.mapMovement.latestPoint.lat = lastRoute.lat;
            this.mapMovement.latestPoint.lng = lastRoute.lng;
          } else {
            this.mapMovement.latestPoint.lat = null;
            this.mapMovement.latestPoint.lng = null;
          }

          this.loadingRoute = false;
        })
        .catch(error => {
          console.log(error);
          this.$Message.error({
            content: "Failed to load GPS data",
            duration: 10,
            closable: true
          });
        })
        .then(() => {
          this.loadingRoute = false;
        });
    },
    loadRunSheets() {
      axios.get(`/carry-freight/run-sheets/get-run-sheets`).then(response => {
        this.runSheets = response.data.data;
        // console.log(this.runSheets);
      });
    },
    postRunSheet() {
      this.$validator.validateAll().then(isValid => {
        if (!this.startLocation || !this.endLocation) {
          this.startLocationError = true;
          this.endLocationError = true;
          return;
        }

        if (this.schedules.length === 0) {
          this.$swal({
            title: "Please drag and drop the legs to the schedule section",
            type: "error"
          });
          return;
        }

        if (isValid) {
          axios
            .post("/carry-freight/run-sheets/store", {
              truck_id: this.truckAllocation.id,
              plate: this.truckAllocation.plate,
              start_date: this.startDate,
              end_date: this.endDate,
              start_location_lat: this.startLocation.lat,
              start_location_lng: this.startLocation.lng,
              end_location_lat: this.endLocation.lat,
              end_location_lng: this.endLocation.lng,
              schedules: this.schedules.map((schedule, idx) => {
                schedule.order = Number(idx) + 1;
                return schedule;
              })
            })
            .then(res => {
              let response = res.data;
              if (response.success) {
                this.$swal({
                  title: response.message,
                  type: "success"
                }).then(() => {
                  window.location.reload();
                });
              } else {
                this.$swal({
                  title: response.message,
                  type: "error"
                });
              }
            })
            .catch(err => {
              Event.$emit("serverError");
              console.log(err);
            });
        }
      });
    },
    onStartLocationChanged(location) {
      this.startLocation = location;
    },
    onEndLocationChanged(location) {
      this.endLocation = location;
    },
    onStartLocationTextChanged() {
      this.startLocationError = false;
    },
    onEndLocationTextChanged() {
      this.endLocationError = false;
    },
    getAddressData: function(addressData, placeResultData, id) {
      this.address = addressData;
    },
    getLegs() {
      this.loadingLegs = true;
      this.schedules = [];

      axios
        .get(
          `/carry-freight/run-sheets/getLegs?truck=${this.truckAllocation.id}`
        )
        .then(response => {
          this.legs = response.data;

          this.legsData = this.legs.data.flatMap(leg => {
            if (leg.is_pickup && leg.is_delivery) {
              let pickupLeg = JSON.parse(JSON.stringify(leg));
              pickupLeg.stage = 1;

              let deliveryLeg = JSON.parse(JSON.stringify(leg));
              deliveryLeg.stage = 2;
              return [pickupLeg, deliveryLeg];
            }
          });

          this.loadingLegs = false;
        })
        .catch(e => {
          Event.$emit("serverError");
          console.log(e);
        });
    },
    loadTrucks() {
      this.loadingTruck = true;
      api.get("/carrier/gps/trucks").then(response => {
        this.trucks = response.data.data.map(truck => {
          return {
            id: truck.id,
            plate: truck.plate,
            display_name: truck.display_name
          };
        });
        this.loadingTruck = false;
      });
    },
    nameWithLang({ plate, display_name }) {
      return plate ? `${display_name} - (${plate})` : `${display_name}`;
    },
    runSheetLabel({ plate, truck_id }) {
      return `Run Sheet for Plate ${plate} - Truck ID (${truck_id}) `;
    },
    handleRunSheetAllocation(value, id) {
      this.isMapLoading = false;

      setTimeout(() => {
        this.mapMovement.pickup.lat = value.start_location_lat;
        this.mapMovement.pickup.lng = value.start_location_lng;

        this.mapMovement.delivery.lat = value.end_location_lat;
        this.mapMovement.delivery.lng = value.end_location_lng;

        this.loadRoute(
          moment()
            .startOf("day")
            .format("YYYY-MM-DD HH:mm:ss"),
          moment()
            .endOf("day")
            .format("YYYY-MM-DD HH:mm:ss")
        );

        this.isMapLoading = true;
      }, 1000);

      // this.isMapLoading = false;
      // console.log(this.mapMovement);
    },
    add: function() {
      this.list.push({ name: "Juan" });
    },
    replace: function() {
      this.list = [{ name: "Edgard" }];
    },
    clone: function(el) {
      return {
        name: el.name + " cloned"
      };
    },
    log: function(evt) {
      window.console.log(evt);
    }
  }
};
</script>
